import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@edytech/shared/types';

// Environment variables
const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Token storage keys
const TOKEN_KEY = 'edytech_access_token';
const REFRESH_TOKEN_KEY = 'edytech_refresh_token';

// Token management functions
export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

const sessionExpiredEvent = 'edytech:session-expired';
const tokensRefreshedEvent = 'edytech:tokens-refreshed';

const decodeTokenPayload = (token: string): { exp?: number } | null => {
  try {
    const encodedPayload = token.split('.')[1];
    const payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - payload.length % 4) % 4);
    return JSON.parse(atob(payload + padding));
  } catch {
    return null;
  }
};

export const isTokenValid = (): boolean => {
  const token = getAccessToken();
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  return typeof payload?.exp === 'number' && payload.exp * 1000 > Date.now();
};

export const getRefreshTokenExpiry = (): number | null => {
  const token = getRefreshToken();
  const payload = token ? decodeTokenPayload(token) : null;
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
};

let refreshPromise: Promise<string> | null = null;

export const refreshAccessToken = async (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token');

      const response = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      setTokens(accessToken, newRefreshToken);
      window.dispatchEvent(new Event(tokensRefreshedEvent));
      return accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const expireSession = (): void => {
  clearTokens();
  window.dispatchEvent(new Event(sessionExpiredEvent));
};

export const onSessionExpired = (handler: () => void): (() => void) => {
  window.addEventListener(sessionExpiredEvent, handler);
  return () => window.removeEventListener(sessionExpiredEvent, handler);
};

export const onTokensRefreshed = (handler: () => void): (() => void) => {
  window.addEventListener(tokensRefreshedEvent, handler);
  return () => window.removeEventListener(tokensRefreshedEvent, handler);
};

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: Add token to headers
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // If error is 401 and not already retried, try refresh token
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const accessToken = await refreshAccessToken();
        
        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear local auth state and require sign-in.
        expireSession();
        window.location.href = '/?session=expired';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Typed API methods
export async function apiGet<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<ApiResponse<T>>(endpoint, { params });
  return response.data.data;
}

/**
 * Same as `apiGet` but returns the FULL response body instead of the unwrapped
 * `data` field. Use for endpoints whose payload shape is the top-level body
 * itself (e.g. paginated lists that include a top-level `pagination` field).
 */
export async function apiGetResponse<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T>(endpoint, { params });
  return response.data;
}

export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(endpoint, data);
  return response.data.data;
}

export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<ApiResponse<T>>(endpoint, data);
  return response.data.data;
}

export async function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<ApiResponse<T>>(endpoint, data);
  return response.data.data;
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  const response = await apiClient.delete<ApiResponse<T>>(endpoint);
  return response.data.data;
}